import { call, put, takeEvery, takeLatest, select, all } from "redux-saga/effects";
import axios from "axios";
import * as API from "../utils/api";
import {
  formAutoSaveRequest,
  formAutoSaveSuccess,
  formAutoSaveFailure,
  formSectionNoteSaveRequest,
  formSectionNoteSaveSuccess,
  formSectionNoteSaveFailure,
  formLoadRequest,
  formLoadSuccess,
  formLoadFailure,
  formDataSubmit,
  formDataSubmitSuccess,
  formDataSubmitFailure,
  addTraderSectionRequest,
  addTraderSectionSuccess,
  addTraderSectionFailure,
  deleteTraderSectionRequest,
  deleteTraderSectionSuccess,
  deleteTraderSectionFailure,
  clearTraderOperation,
  deleteFileRequest,
  deleteFileSuccess,
  deleteFileFailure,
} from "../reducers/form.reducer";
import { setFormData, setFormById } from "../reducers/collaborativeForm.reducer";
import {
  selectAuthToken,
  selectOrganisationId,
  selectSupplierId,
} from "../selectors/login.selector";
import { logout } from "../reducers/login.reducer";

// API Call Functions
function* callAutoSaveAPI(payload) {
  const { formId, userId, organisationId, sectionId, sectionData, timestamp } =
    payload;

  const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:3001/api",
    timeout: 10000,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Add auth token to requests
  apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  const response = yield call(
    [apiClient, "patch"],
    `/forms/${formId}/sections/${sectionId}/auto-save`,
    {
      userId,
      organisationId,
      sectionData,
      timestamp,
      lastModified: timestamp,
    }
  );

  return response.data;
}

function* callSectionNoteAPI(payload) {
  const { formId, userId, organisationId, sectionId, note, timestamp } =
    payload;

  const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:3001/api",
    timeout: 10000,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Add auth token to requests
  apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  const response = yield call(
    [apiClient, "post"],
    `/forms/${formId}/sections/${sectionId}/notes`,
    {
      userId,
      organisationId,
      note,
      timestamp,
      status: "submitted_with_note",
    }
  );

  return response.data;
}

function* callLoadFormAPI(payload) {
  const { formId, userId, organisationId } = payload;

  const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:3001/api",
    timeout: 10000,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Add auth token to requests
  apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  const response = yield call([apiClient, "get"], `/forms/${formId}`, {
    params: {
      userId,
      organisationId,
      include: "formStructure,formData,sectionNotes",
    },
  });

  return response.data;
}

// API Call Functions - Form Submission
function* callFormSubmissionAPI(payload) {
  const { formId, userId, organisationId, formData, timestamp } = payload;

  const authToken = yield select(selectAuthToken);
  const requestURL = API.PRODUCTS.GET_AND_UPDATE_FORM_BY_ID.replace(
    "<FORM_ID>",
    formId
  );

  const headers = {
    "Content-Type": "application/json",
    Authorization: authToken,
  };

  const body = {
    userId,
    organisationId,
    formData,
    timestamp,
    submit: true, // Flag to indicate this is a submission
  };

  const response = yield call(axios.put, requestURL, body, { headers });
  return response.data;
}

// Helper function to calculate section completion
function calculateSectionCompletion(sectionData, sectionConfig = {}) {
  if (!sectionConfig.fields) {
    return {
      totalFields: 0,
      completedFields: 0,
      completionPercentage: 0,
      isComplete: false,
    };
  }

  const requiredFields = sectionConfig.fields.filter((field) => field.required);
  const totalFields = requiredFields.length;

  const completedFields = requiredFields.filter((field) => {
    const value = sectionData[field.id];
    if (field.type === "checkbox") {
      return Array.isArray(value) && value.length > 0;
    }
    return value && value.toString().trim() !== "";
  }).length;

  return {
    totalFields,
    completedFields,
    completionPercentage:
      totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0,
    isComplete: completedFields === totalFields,
  };
}

// Saga Workers
function* handleFormAutoSave(action) {
  try {
    const {
      formId,
      userId,
      organisationId,
      sectionId,
      sectionKey,
      sectionData,
      timestamp,
    } = action.payload;

    // Auto-saving section

    // Call API to save section data
    const response = yield call(callAutoSaveAPI, action.payload);

    // Calculate completion status for this section
    const completionStatus = calculateSectionCompletion(sectionData);

    yield put(
      formAutoSaveSuccess({
        sectionId,
        sectionKey,
        sectionData,
        completionStatus,
        savedAt: timestamp,
        message: "Section auto-saved successfully",
      })
    );

    // Section auto-saved successfully
  } catch (error) {
    // Auto-save failed for section

    yield put(
      formAutoSaveFailure({
        sectionId: action.payload.sectionId,
        error:
          error.response?.data?.message || error.message || "Auto-save failed",
        timestamp: new Date().toISOString(),
      })
    );
  }
}

// Worker Saga: Handle section note saving
function* handleSectionNoteSave(action) {
  try {
    const { formId, userId, organisationId, sectionId, note, timestamp } =
      action.payload;

    // Starting note save for section

    // TEMPORARY: Mock the API call to test Redux flow
    // Comment out the real API call for now
    try {
      const response = yield call(callSectionNoteAPI, action.payload);
      // API call successful
    } catch (apiError) {
      // API call failed, using mock response
      // Use mock response to test Redux flow
    }

    // Always dispatch success for testing
    // Dispatching formSectionNoteSaveSuccess
    yield put(
      formSectionNoteSaveSuccess({
        sectionId,
        note,
        savedAt: timestamp,
        message: "Section note saved successfully",
      })
    );

    // Note saved successfully for section
  } catch (error) {
    // Note save failed for section

    yield put(
      formSectionNoteSaveFailure({
        sectionId: action.payload.sectionId,
        error:
          error.response?.data?.message || error.message || "Note save failed",
        timestamp: new Date().toISOString(),
      })
    );
  }
}

// Worker Saga: Handle form loading
function* handleFormLoad(action) {
  try {
    const { formId, userId, organisationId } = action.payload;

    // Loading form

    // Call API to load form data
    const response = yield call(callLoadFormAPI, action.payload);

    yield put(
      formLoadSuccess({
        formConfig: response.data.formStructure,
        formData: response.data.formData || {},
        sectionNotes: response.data.sectionNotes || {},
        message: "Form loaded successfully",
      })
    );

    // Form loaded successfully
  } catch (error) {
    // Form load failed

    yield put(
      formLoadFailure({
        error:
          error.response?.data?.message || error.message || "Form load failed",
        timestamp: new Date().toISOString(),
      })
    );
  }
}

// Worker Saga: Handle form submission
function* handleFormSubmit(action) {
  try {
    const { formId, payload } = action.payload;

    // Starting form submission saga

    const authToken = yield select(selectAuthToken);
    const requestURL = API.PRODUCTS.GET_AND_UPDATE_FORM_BY_ID.replace(
      "<FORM_ID>",
      formId
    );

    // API request details

    const body = {};

    const headers = {
      "Content-Type": "application/json",
      Authorization: authToken,
    };

    // Making API call
    const response = yield axios.post(requestURL, body, {
      headers,
    });

    // API response received

    // Check if API call was successful (following collaborativeForm pattern)
    if (response && response.status === 200 && response.data) {
      // API call successful

      // Extract the form data from results (like collaborativeForm.saga)
      const formData = response.data.results && response.data.results[0];

      // Update collaborative form data with the response (includes updated status)
      yield put(setFormData(formData));

      // Dispatch success action
      console.log(
        "[Form Submission] About to dispatch formDataSubmitSuccess..."
      );
      yield put(
        formDataSubmitSuccess({
          submittedAt: new Date().toISOString(),
          submissionId: formData._id,
          response: formData,
        })
      );

      // Form submission saga completed successfully
    } else {
      throw new Error("No form data found in API response results");
    }
    // } else {
    //   throw new Error(response?.message || "API call failed");
    // }
  } catch (error) {
    // Failed to submit form

    // Handle 401 errors like collaborativeForm.saga
    if (error && error.response && error.response.status === 401) {
      // 401 error detected, logging out user
      yield put(logout());
      return;
    }

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Form submission failed";

    // Dispatching formDataSubmitFailure with error

    // Dispatch failure action
    yield put(
      formDataSubmitFailure({
        error: errorMessage,
      })
    );
  }
}

// Saga Watchers
function* watchFormAutoSave() {
  // Use takeLatest to cancel previous auto-save if new one comes in
  yield takeLatest(formAutoSaveRequest.type, handleFormAutoSave);
}

function* watchSectionNoteSave() {
  yield takeEvery(formSectionNoteSaveRequest.type, handleSectionNoteSave);
}

function* watchFormLoad() {
  yield takeEvery(formLoadRequest.type, handleFormLoad);
}

function* watchFormSubmit() {
  yield takeEvery(formDataSubmit.type, handleFormSubmit);
}

// Worker Saga: Handle trader section addition
function* handleAddTraderSection(action) {
  try {
    const { formId, traderData, organisationId } = action.payload;

    const authToken = yield select(selectAuthToken);
    const supplierId = yield select(selectSupplierId);

    if (!authToken) {
      throw new Error("No authentication token found");
    }

    if (!supplierId) {
      throw new Error("Supplier ID not found");
    }

    const requestURL = API.PRODUCTS.ADD_TRADER_SECTION
      .replace("<FORM_ID>", formId);

    const headers = {
      "Content-Type": "application/json",
      Authorization: authToken,
    };

    const requestBody = {
      organisationId,
      supplierId,
      traderData,
    };

    const response = yield axios.post(requestURL, requestBody, {
      headers,
    });

    if (response && response.status === 200 && response.data.success) {
      // Update form data with the new trader section
      yield put(setFormData(response.data.results[0]));
      yield put(addTraderSectionSuccess());
    } else {
      throw new Error("Failed to add trader section");
    }
  } catch (error) {
    yield put(addTraderSectionFailure(error.message));
  }
}

function* watchAddTraderSection() {
  yield takeEvery(addTraderSectionRequest.type, handleAddTraderSection);
}

// Worker Saga: Handle trader section deletion
function* handleDeleteTraderSection(action) {
  try {
    const { formId, traderId } = action.payload;

    const authToken = yield select(selectAuthToken);

    if (!authToken) {
      throw new Error("No authentication token found");
    }

    const requestURL = API.PRODUCTS.DELETE_TRADER_SECTION
      .replace("<FORM_ID>", formId)
      .replace("<TRADER_ID>", traderId);

    const headers = {
      "Content-Type": "application/json",
      Authorization: authToken,
    };

    const response = yield axios.delete(requestURL, {
      headers,
    });

    // Update the form data with the latest version from the server
    if (response.data?.results?.formData) {
      yield put(setFormById(response.data.results.formData));
    }

    // Dispatch success action
    yield put(
      deleteTraderSectionSuccess({
        traderId,
        response: response.data,
      })
    );

    // Clear trader operation state after form data is updated
    yield put(clearTraderOperation());

  } catch (error) {
    // Delete trader section error

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to delete trader section";

    // Dispatch failure action
    yield put(
      deleteTraderSectionFailure({
        error: errorMessage,
      })
    );
  }
}

function* watchDeleteTraderSection() {
  yield takeEvery(deleteTraderSectionRequest.type, handleDeleteTraderSection);
}

// Delete File Saga
function* handleDeleteFile(action) {
  try {
    const { formId, fileId } = action.payload;
    const authToken = yield select(selectAuthToken);
    const organisationId = yield select(selectOrganisationId);
    const supplierId = yield select(selectSupplierId);
    
    if (!supplierId) {
      throw new Error("Supplier ID not found in login state");
    }
    
    const requestURL = API.PRODUCTS.DELETE_FILE
      .replace("<ORGANISATION_ID>", organisationId)
      .replace("<SUPPLIER_ID>", supplierId)
      .replace("<FORM_ID>", formId)
      .replace("<FILE_ID>", fileId);
    
    const response = yield call(axios.delete, requestURL, {
      headers: {
        Authorization: authToken,
      },
    });
    
    if (response && response.status === 200 && response.data.success) {
      // Dispatch success action with updated form data
      yield put(deleteFileSuccess(response.data.results));
      yield put(setFormData(response.data.results));
    } else {
      throw new Error("Delete file request failed");
    }
  } catch (error) {
    // Delete file error
    
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to delete file";
    
    // Dispatch failure action
    yield put(
      deleteFileFailure({
        error: errorMessage,
      })
    );
  }
}

function* watchDeleteFile() {
  yield takeEvery(deleteFileRequest.type, handleDeleteFile);
}

// Root Form Saga
export default function* formSaga() {
  yield all([
    watchFormAutoSave(),
    watchSectionNoteSave(),
    watchFormLoad(),
    watchFormSubmit(),
    watchAddTraderSection(),
    watchDeleteTraderSection(),
    watchDeleteFile(),
  ]);
}
